import { get, toString } from 'lodash';
import { ACTION_PREFIX, TAB, CARRIER } from '../../constants';
import dropdownStateSelector from './dropdownStateSelector';
import { selectCarrier } from '../carriers';
import { selectPlan } from '../plans';
import invariant from 'invariant';
import cookie from 'js-cookie';
import url from 'url';
import getScanCardState from '../../data/scan-card/scanCardSelector';
import { fetchPlansIfNeededDropdown } from '../plans/plansActions';
import { insuranceTypeSelector } from '../insurance-type';
import { fetchPopularPlansIfNeededDropdown } from '../popular-plans';
import { FLOW_SOURCE_ENUM } from './dropdownStateTracking';
import { EVENT_INITIATORS } from '@zocdoc/frontend-metrics';

export const DROPDOWN_STATE = `${ACTION_PREFIX}/DROPDOWN_STATE`;
export const SELECT_INSURANCE_PLAN = `${ACTION_PREFIX}/SELECT_INSURANCE_PLAN`;
export const SELECT_INSURANCE_CARRIER = `${ACTION_PREFIX}/SELECT_INSURANCE_CARRIER`;
export const SET_SHOW_SCAN_INSURANCE_CARD_BUTTON = `${ACTION_PREFIX}/SET_SHOW_SCAN_INSURANCE_CARD_BUTTON`;
export const USE_V2_CARD_SCANNING_FLOW = `${ACTION_PREFIX}/USE_V2_CARD_SCANNING_FLOW`;

export const SET_SCAN_CARD_ATTEMPTED = `${ACTION_PREFIX}/SET_SCAN_CARD_ATTEMPTED`;
export const SET_SHOW_SCAN_INSURANCE_ERROR_MODAL = `${ACTION_PREFIX}/SET_SHOW_SCAN_INSURANCE_ERROR_MODAL`;
export const TAB_SELECTION = `${ACTION_PREFIX}/TAB_SELECTION`;
export const ALL_PLANS = `${ACTION_PREFIX}/ALL_PLANS`;
export const TEXTBOX_CHANGE = `${ACTION_PREFIX}/TEXTBOX_CHANGE`;
export const TRACK_TEXTBOX_CHANGE = `${ACTION_PREFIX}/TRACK_TEXTBOX_CHANGE`;
export const REGISTER_CALLBACK = `${ACTION_PREFIX}/REGISTER_CALLBACK`;
export const REGISTER_GA_CATEGORY = `${ACTION_PREFIX}/REGISTER_GA_CATEGORY`;
export const REGISTER_LOGGING_SECTION = `${ACTION_PREFIX}/REGISTER_LOGGING_SECTION`;
export const SHOW_ALL_ITEMS = `${ACTION_PREFIX}/SHOW_ALL_ITEMS`;
export const REGISTER_TRACKING = `${ACTION_PREFIX}/REGISTER_TRACKING`;
export const AUTOCOMPLETE_SELECTION = `${ACTION_PREFIX}/AUTOCOMPLETE_SELECTION`;
export const REGISTER_AWSURL = `${ACTION_PREFIX}/REGISTER_AWSURL`;
export const REGISTER_CLEARDATA_URL = `${ACTION_PREFIX}/REGISTER_CLEARDATA_URL`;
export const TAB_CHANGE_MARKED = `${ACTION_PREFIX}/TAB_CHANGE_MARKED`;
export const OPEN_MARKED = `${ACTION_PREFIX}/OPEN_MARKED`;
export const CLEARED_CARRIER = `${ACTION_PREFIX}/CLEARED_CARRIER`;
export const CHANGE_FOCUS = `${ACTION_PREFIX}/CHANGE_FOCUS`;
export const SET_DIRECTORY = `${ACTION_PREFIX}/SET_DIRECTORY`;
export const OPEN_SCAN_CARD = `${ACTION_PREFIX}/OPEN_SCAN_CARD`;
export const CLOSE_SCAN_CARD = `${ACTION_PREFIX}/CLOSE_SCAN_CARD`;
export const SCAN_CARD_AVAILABLE = `${ACTION_PREFIX}/SCAN_CARD_AVAILABLE`;
export const REGISTER_RECORD_AB_PLAN_CHOSEN_BY_USER = `${ACTION_PREFIX}/REGISTER_RECORD_AB_PLAN_CHOSEN_BY_USER`;
export const REGISTER_RECORD_AB_CARRIER_CHOSEN_BY_USER = `${ACTION_PREFIX}/REGISTER_RECORD_AB_CARRIER_CHOSEN_BY_USER`;

export function closeDropdown() {
    return (dispatch, getState) => {
        const state = getState();
        const dropdownState = dropdownStateSelector(state);
        if (dropdownState.isOpen) {
            // "onBlur" notification if insurance picker is cleared
            const { carrier } = dropdownState;
            if (!carrier) {
                clearValuesCallback(getState);
            }
            return dispatch({
                type: DROPDOWN_STATE,
                payload: { isOpen: false },
            });
        }
    };
}

export function openDropdown() {
    return (dispatch, getState) => {
        const state = getState();
        const { isOpen } = dropdownStateSelector(state);
        if (!isOpen) {
            return dispatch({
                type: DROPDOWN_STATE,
                payload: { isOpen: true },
            });
        }
    };
}

export function openScanCard(e) {
    // This callback is submitting the form on patient-web-application homepage; we don't want that
    if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
    }

    return (dispatch, getState) => {
        const state = getState();
        const dropdownState = dropdownStateSelector(state);

        if (!dropdownState.isScanCardOpen) {
            return dispatch({ type: OPEN_SCAN_CARD });
        }
    };
}

export function closeScanCard(flowLocation) {
    invariant(flowLocation, 'patient location in flow must be passed');

    return dispatch => {
        if (flowLocation === 'card upload screen') {
            dispatch(setScanCardAttempted());
        }

        return dispatch({ type: CLOSE_SCAN_CARD });
    };
}

export function setPlan(plan, _, flowSource = null) {
    return (dispatch, getState) => {
        const dropdownState = getDropdownState(getState);

        const { recordAbPlanChosenByUser } = dropdownState;

        if (recordAbPlanChosenByUser && plan && plan.id > 0 && flowSource.sourceType === EVENT_INITIATORS.USER) {
            recordAbPlanChosenByUser();
        }

        return dispatch({
            type: SELECT_INSURANCE_PLAN,
            payload: { plan },
        });
    };
}

export function setCarrier(carrier, _, flowSource = null) {
    return (dispatch, getState) => {
        const dropdownState = getDropdownState(getState);

        const { recordAbCarrierChosenByUser } = dropdownState;

        if (
            recordAbCarrierChosenByUser &&
            carrier &&
            carrier.id > 0 &&
            flowSource.sourceType === EVENT_INITIATORS.USER
        ) {
            recordAbCarrierChosenByUser();
        }

        return dispatch({
            type: SELECT_INSURANCE_CARRIER,
            payload: { carrier },
        });
    };
}

export function setDirectoryId(directoryId) {
    return {
        type: SET_DIRECTORY,
        payload: directoryId,
    };
}

export function setV2InsuranceCardScanResultFlow(useNewFlow = false) {
    return {
        type: USE_V2_CARD_SCANNING_FLOW,
        payload: useNewFlow,
    };
}

export function setShowScanInsuranceCardButton(showScanInsuranceCardButton = false) {
    return {
        type: SET_SHOW_SCAN_INSURANCE_CARD_BUTTON,
        payload: showScanInsuranceCardButton,
    };
}

export function setScanCardAttempted() {
    return {
        type: SET_SCAN_CARD_ATTEMPTED,
    };
}

const setTab = (tab, hideBcbs) => {
    return (dispatch, getState) => {
        const state = getState();
        const dropdownState = dropdownStateSelector(state);
        const canSeeBcbs = hideBcbs ? false : dropdownState.canSeeBcbs || tab === TAB.BCBS;

        if (dropdownState.tab === tab && canSeeBcbs === dropdownState.canSeeBcbs) {
            return;
        }

        return dispatch({
            type: TAB_SELECTION,
            payload: {
                tab,
                tabChange: true,
                canSeeBcbs,
            },
        });
    };
};

export function markTabChange() {
    return {
        type: TAB_CHANGE_MARKED,
        payload: false,
    };
}

export function markOpenDropdown() {
    return {
        type: OPEN_MARKED,
        payload: false,
    };
}

export function viewInsuranceTab(hideBcbs) {
    return setTab(TAB.INSURANCE, hideBcbs);
}

export function viewBcbsTab() {
    return setTab(TAB.BCBS);
}

export function viewPlanTab(hideBcbs) {
    return setTab(TAB.PLAN, hideBcbs);
}

export function textBoxChange(event) {
    return {
        type: TEXTBOX_CHANGE,
        payload: { typedValue: event.target.value },
    };
}

export function chooseCarrier(carrier, shouldLog, shouldFetchPopularPlans = true, flowSource = null) {
    return (dispatch, getState) => {
        const dropdownState = getDropdownState(getState);
        const selectedCarrier = dropdownState.carrier || {};
        const { tab } = dropdownState;

        if (!carrier) {
            dispatch(selectPlan('-1'));
            setPlan(null)(dispatch, getState);
            dispatch(selectCarrier('-1'));
            setCarrier(null)(dispatch, getState);
            viewInsuranceTab()(dispatch, getState);
            carrierCallback(null, getState);
            return;
        }

        if (selectedCarrier.id !== carrier.id) {
            dispatch(selectPlan('-1'));
            setPlan(null)(dispatch, getState);
        }

        dispatch(selectCarrier(carrier.id));
        setCarrier(carrier, shouldLog, flowSource)(dispatch, getState);
        dispatch(fetchPlansIfNeededDropdown());

        if (shouldFetchPopularPlans) {
            dispatch(fetchPopularPlansIfNeededDropdown());
        }

        if (carrier.id === CARRIER.BLUE_CROSS_BLUE_SHIELD) {
            viewBcbsTab()(dispatch, getState);
        } else {
            const hideBcbs = tab !== TAB.BCBS && carrier.id !== CARRIER.BLUE_CROSS_BLUE_SHIELD;
            viewPlanTab(hideBcbs)(dispatch, getState);
        }

        carrierCallback(carrier, getState);
    };
}

function getDropdownState(getState) {
    const state = getState();
    return dropdownStateSelector(state);
}

function successCallback(carrier, plan, getState, shouldLog = true) {
    const state = getState();
    const dropdownState = dropdownStateSelector(state);
    const insuranceType = insuranceTypeSelector(state);

    if (!dropdownState.successCallback) {
        return;
    }

    dropdownState.successCallback({
        carrier,
        plan: plan || carrier,
        insuranceType,
        shouldLog,
    });
}

function carrierCallback(carrier, getState, shouldLog = true) {
    const state = getState();
    const dropdownState = dropdownStateSelector(state);
    const insuranceType = insuranceTypeSelector(state);

    if (!dropdownState.carrierCallback) {
        return;
    }

    dropdownState.carrierCallback({
        carrier,
        insuranceType,
        shouldLog,
    });
}

function clearValuesCallback(getState) {
    const dropdownState = getDropdownState(getState);

    if (!dropdownState.clearValuesCallback) {
        return;
    }

    dropdownState.clearValuesCallback();
}

export function chooseNonInsuranceOption(option, shouldLog, flowSource) {
    return (dispatch, getState) => {
        dispatch(selectPlan('-1'));
        setPlan(null, shouldLog, flowSource)(dispatch, getState);
        dispatch(selectCarrier(option.id));
        setCarrier(option, shouldLog, flowSource)(dispatch, getState);
        viewInsuranceTab(true)(dispatch, getState);
        closeDropdown()(dispatch, getState);
        successCallback(option, null, getState);
    };
}

function getCleardataUrl(dropdownState) {
    return url.parse(process.env.CLEARDATA_API_HOST || dropdownState.CLEARDATA_API_HOST || '');
}

function tryToGetFullstoryUrl() {
    if (window && window._fs_loaded) {
        return window[window._fs_loaded].getCurrentSessionURL();
    }

    return '';
}

export function logCarrierAndPlanSelection(getState) {
    const dropDownState = getDropdownState(getState);
    const apiUrl = getCleardataUrl(dropDownState);
    const scanCardState = getScanCardState(getState());

    if (get(scanCardState, 'correlation_id') === null) {
        return false;
    }

    const dataToLog = {
        session_id: cookie.get('bsid'),
        useragent: get(window, 'navigator.userAgent'),
        fullstory_url: tryToGetFullstoryUrl(),
        client_timestamp: new Date().toISOString(),
        page_category: get(dropDownState, 'gaCategory'),
        correlation_id: get(scanCardState, 'correlation_id'),
        carrier_id: get(dropDownState, 'carrier.id'),
        carrier_is_from_prediction: get(scanCardState, 'carrierPositionSelectedByPatient') !== -1,
        carrier_display_position: get(scanCardState, 'carrierPositionSelectedByPatient'),
        plan_id: get(dropDownState, 'plan.id'),
        plan_is_from_prediction: get(scanCardState, 'planPositionSelectedByPatient') !== -1,
        plan_display_position: get(scanCardState, 'planPositionSelectedByPatient'),
    };

    fetch(
        url.format({
            protocol: apiUrl.protocol,
            host: apiUrl.host,
            pathname: '/card-extraction/v1/events',
        }),
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                version: '1.0.0',
                type: 'insurance_prediction_selection',
                data: dataToLog,
            }),
        }
        // eslint-disable-next-line no-console
    ).catch(err => console.error(err));

    return true;
}

export function choosePlan(plan, selectedCarrier, shouldLog = true, flowSource = null) {
    return (dispatch, getState) => {
        dispatch(selectPlan(plan.id));
        setPlan(plan, shouldLog, flowSource)(dispatch, getState);
        viewInsuranceTab()(dispatch, getState);
        closeDropdown()(dispatch, getState);

        const carrier = selectedCarrier || getDropdownState(getState).carrier;

        logCarrierAndPlanSelection(getState);

        successCallback(carrier, plan, getState, shouldLog);
    };
}

export function clearCarrierAndPlan() {
    return (dispatch, getState) => {
        dispatch(selectCarrier('-1'));
        setCarrier(null)(dispatch, getState);
        dispatch(selectPlan('-1'));
        setPlan(null)(dispatch, getState);
        viewInsuranceTab(true)(dispatch, getState);
        dispatch({ type: CLEARED_CARRIER });
        carrierCallback(null, getState);
    };
}

export function chooseSearchItem(item) {
    return (dispatch, getState) => {
        const dropdownState = getDropdownState(getState);
        const selectedCarrier = dropdownState.carrier || {};

        const carrier = {
            id: toString(item.carrier_id),
            name: item.carrier_name,
        };

        const shouldLogSelectedCarrierEvent = !(
            carrier.id === selectedCarrier.id && carrier.name === selectedCarrier.name
        );

        dispatch(selectCarrier(carrier.id));
        setCarrier(carrier, shouldLogSelectedCarrierEvent, FLOW_SOURCE_ENUM.Autocomplete)(dispatch, getState);
        carrierCallback(carrier, getState);

        if (carrier.id === CARRIER.BLUE_CROSS_BLUE_SHIELD) {
            viewBcbsTab()(dispatch, getState);
        } else {
            viewPlanTab(true)(dispatch, getState);
        }

        if (item.plan_id) {
            const plan = {
                id: toString(item.plan_id),
                name: item.displayPlanName,
            };
            choosePlan(plan, carrier, true, FLOW_SOURCE_ENUM.Autocomplete)(dispatch, getState);
        } else if (!selectedCarrier || !selectedCarrier.carrier || selectedCarrier.carrier.id !== item.carrier_id) {
            dispatch(selectPlan('-1'));
            setPlan(null)(dispatch, getState);
        }
    };
}

export function registerCallbacks(callbacks) {
    return {
        type: REGISTER_CALLBACK,
        payload: callbacks,
    };
}

export function registerAwsApiUrl(urlPayload) {
    return {
        type: REGISTER_AWSURL,
        payload: urlPayload,
    };
}

export function registerCleardataUrl(urlPayload) {
    return {
        type: REGISTER_CLEARDATA_URL,
        payload: urlPayload,
    };
}

export function registerGoogleAnalyticsCategory(category) {
    return {
        type: REGISTER_GA_CATEGORY,
        payload: category,
    };
}

export function registerRecordAbCarrierChosenByUser(recordFunc) {
    return {
        type: REGISTER_RECORD_AB_CARRIER_CHOSEN_BY_USER,
        payload: recordFunc,
    };
}

export function registerRecordAbPlanChosenByUser(recordFunc) {
    return {
        type: REGISTER_RECORD_AB_PLAN_CHOSEN_BY_USER,
        payload: recordFunc,
    };
}

export function registerLoggingSection(section) {
    return {
        type: REGISTER_LOGGING_SECTION,
        payload: section,
    };
}

export function registerTracking(tracking) {
    return {
        type: REGISTER_TRACKING,
        payload: tracking,
    };
}

export function showAllForTab(tab, shouldShow) {
    return {
        type: SHOW_ALL_ITEMS,
        payload: {
            tab,
            shouldShow,
        },
    };
}

export function changeFocus(nextFocusIndex) {
    return {
        type: CHANGE_FOCUS,
        payload: {
            nextFocusIndex,
        },
    };
}

export function setShowScanCardErrorModal(shouldShow) {
    // TODO Chris when do we hide the scan card button?
    return {
        type: SET_SHOW_SCAN_INSURANCE_ERROR_MODAL,
        payload: { shouldShow },
    };
}

export function recordScanCardAvailable() {
    return { type: SCAN_CARD_AVAILABLE };
}
